<script setup lang="ts">
import type { UniqueLabel } from "@lxc/app-device-common";
import { ref } from "vue";
import { typeOptions as defaultRangeOptions } from "~/constants/deviceFilters.config";
import filtersUtils from "~/core/utils/filters.utils";
import type { DtwinConnectivityStatus } from "~/modules/dtwin/models/DtwinConnectivityStatus.enum";
import type {
  FilterFormSection,
  FilterOptions,
  FilterSelectionValue,
  FiltersSelection,
  Option,
} from "~/types";
import { FilterInputType, Filters } from "~/types";

const props = defineProps<{
  organizationCode?: number;
  filters: FiltersSelection;
  deviceTypeOptions?: FilterOptions;
  sectorOptions?: FilterOptions;
  lifecycleStatesOptions?: FilterOptions;
}>();

const emit = defineEmits(["change", "enter"]);

const { t } = useI18n();

const connectivityTypes: Ref<DtwinConnectivityStatus[]> = ref([]);
const appliedConnectivityTypes: Ref<DtwinConnectivityStatus[]> = ref([]);
const deviceTypes: Ref<string[]> = ref([]);
const appliedDeviceTypes: Ref<string[]> = ref([]);

const deviceTypeLabel = ref(t("dtwins.filters.deviceType"));
const lifecycleLabel = ref(t("dtwins.filters.lifecycleState"));
const connectivityLabel = ref(t("dtwins.filters.connectivity.label"));

const filterDeviceTypeOptions: ComputedRef<Option[]> = computed(() => {
  return props.deviceTypeOptions?.options ?? defaultRangeOptions.options;
});

const filterLifecycleStatesOptions: ComputedRef<Option[]> = computed(() => {
  return props.lifecycleStatesOptions?.options ?? [];
});

const selectedFiltersByTypes = computed(() => [
  connectivityTypes.value,
  deviceTypes.value,
]);

const selectedFilterTags = computed((): UniqueLabel[] => {
  return buildFilterTags(connectivityTypes.value, deviceTypes.value);
});

const appliedFilterTags = computed((): UniqueLabel[] => {
  return buildFilterTags(
    appliedConnectivityTypes.value,
    appliedDeviceTypes.value,
  );
});

const filterFormSections: ComputedRef<FilterFormSection[]> = computed(() => {
  const result: FilterFormSection[] = [];

  result.push({
    disabled: false,
    filter: Filters.DTWIN_MODEL_TYPE,
    footerId: "deviceTypes-footer",
    footerEnabled: true,
    header: deviceTypeLabel.value,
    id: "device-types",
    inputType: FilterInputType.CUSTOM,
    menuLabel: deviceTypeLabel.value,
    tagPrefix: deviceTypeLabel.value,
  });
  result.push({
    disabled: false,
    filter: Filters.DTWINS_IN_LIFE_CYCLE_STATE,
    header: lifecycleLabel.value,
    footerId: "lifecycleStates-footer",
    footerEnabled: true,
    id: "lifecycleStates",
    inputType: FilterInputType.CHECKBOX,
    menuLabel: lifecycleLabel.value,
    options: filterLifecycleStatesOptions,
    tagPrefix: lifecycleLabel.value,
    translate: false,
  });
  result.push({
    disabled: false,
    filter: Filters.DTWIN_CONNECTIVITY_STATUS,
    footerEnabled: true,
    footerId: "connectivity-footer",
    header: connectivityLabel.value,
    id: "connectivity",
    inputType: FilterInputType.CUSTOM,
    menuLabel: connectivityLabel.value,
    tagPrefix: connectivityLabel.value,
  });

  return result;
});

function setFilter(filter: Filters, value: FilterSelectionValue) {
  emit("change", filter, value);
}

function applyFilter() {
  emit("change", Filters.DTWIN_CONNECTIVITY_STATUS, connectivityTypes.value);
  emit("change", Filters.DTWIN_MODEL_TYPE, deviceTypes.value);
  emit("enter");
}

function onAppliedFilterChange() {
  appliedConnectivityTypes.value = connectivityTypes.value;
  appliedDeviceTypes.value = deviceTypes.value;
}

function clearFilter() {
  connectivityTypes.value = [];
  deviceTypes.value = [];
  applyFilter();
}

function buildFilterTags(
  connectivityTypes: DtwinConnectivityStatus[],
  deviceTypes: string[],
) {
  let tagFilters: UniqueLabel[] = [];
  const connectivityTags = connectivityTypes?.reduce(
    (acc: UniqueLabel[], connectivityType) => {
      const filterTag = filtersUtils.getTag(
        connectivityLabel.value,
        connectivityType,
        t(`dtwins.filters.connectivity.${connectivityType}`),
      );
      if (filterTag) {
        acc.push(filterTag);
      }
      return acc;
    },
    [],
  );
  tagFilters = [...tagFilters, ...connectivityTags];
  if (deviceTypes?.length) {
    const deviceTags = deviceTypes?.reduce((acc: UniqueLabel[], deviceType) => {
      const option = props.deviceTypeOptions?.options?.find((currentOption) => {
        return currentOption.value.includes(deviceType);
      });
      const filterTag = filtersUtils.getTag(
        deviceTypeLabel.value,
        deviceType,
        option?.label,
      );
      const findTag = acc.find((tag) => {
        return tag.label === filterTag?.label;
      });
      if (filterTag && !findTag) {
        acc.push(filterTag);
      }
      return acc;
    }, []);
    tagFilters = [...tagFilters, ...deviceTags];
  }
  return tagFilters;
}

function onAppliedTagDeleteClick(tag: string, uid?: string) {
  onDeleteSelectedTag(tag, uid);
  applyFilter();
}

function onDeleteSelectedTag(tag: string, uid?: string) {
  if (uid) {
    connectivityTypes.value = connectivityTypes.value?.filter(
      (connectivityType) => connectivityType !== uid,
    );
    deviceTypes.value = deviceTypes.value?.filter((deviceType) => {
      const foundOption = filterDeviceTypeOptions.value.find((option) => {
        return option.value.includes(uid) && option.value.includes(deviceType);
      });
      return deviceType !== uid && !foundOption?.value.includes(uid);
    });
  }
}

watch(
  () => props.filters.get(Filters.DTWIN_MODEL_TYPE),
  () => {
    deviceTypes.value = (props.filters.get(Filters.DTWIN_MODEL_TYPE)?.value ??
      []) as string[];
  },
  { deep: true },
);
</script>

<template>
  <div class="relative mb-4">
    <filters-component
      :applied-tags="appliedFilterTags"
      :button-wrapper-class="['!pt-0']"
      :filter-sections="filterFormSections"
      :filters="filters"
      :filters-by-type="selectedFiltersByTypes"
      :selected-tags="selectedFilterTags"
      is-button-right
      @change="setFilter"
      @apply="applyFilter"
      @applied-filter-change="onAppliedFilterChange"
      @enter="emit('enter', $event)"
      @reset="clearFilter"
      @delete="onAppliedTagDeleteClick"
      @delete-selected="onDeleteSelectedTag"
    >
      <template #connectivity
        ><dtwin-filter-connectivity v-model="connectivityTypes" />
      </template>

      <template #device-types
        ><dtwin-filter-type
          v-model="deviceTypes"
          :filter-device-type-options="filterDeviceTypeOptions"
        />
      </template>
    </filters-component>
  </div>
</template>
